import React from "react";
import _ from "lodash";

import { NetworkAPI, AutoPilotAPI } from "apis";
import LoadingUI from "../../../common/LoadingUI";
import NetworkInfoHeader from "../../common/NetworkInfoHeader";
import { notify } from "react-notify-toast";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import { getFeaturesSchema } from "./FeaturesSchema";
import SchemaConfigTable from "components/common/SchemaConfigTable";
import ConfigDiffView from "components/control-center/auto-pilot/config/ConfigDiffView";
import PreviewFeaturesAndSaveModal from "./PreviewFeaturesAndSaveModal";
import PageTitle from "components/common/PageTitle";
import { MESSAGE } from "constants/Message";
import { FiExternalLink } from "react-icons/fi";

class NetworkFeaturesViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      errMsg: null,

      appName: null,
      config: null,
      updatedAt: null,

      schemasGrouped: null,
      isEditing: false,

      newConfig: null,
      isSaving: false,
      saveErrMsg: null,

      isReviewModalOpened: false,
    };

    this.handleConfigTextChanged = this.handleConfigTextChanged.bind(this);
    this.handleSaveRawFeatures = this.handleSaveRawFeatures.bind(this);

    this.handleApplyChanges = this.handleApplyChanges.bind(this);

    this.handleCloseReviewModal = this.handleCloseReviewModal.bind(this);
    this.handleOpenReviewModal = this.handleOpenReviewModal.bind(this);
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      const { networkId } = this.props.match.params;
      if (!networkId) {
        throw new Error("Missing Network ID");
      }
      const networkInfo = await NetworkAPI.getNetworkInfo({ networkId });
      if (!networkInfo) {
        throw new Error("Invalid Network");
      }

      // const { defaults, schema } = await NetworkAPI.getFeaturesSchema();
      const defaults = await NetworkAPI.getDefaultNetworkFeatures({
        networkId,
      });

      const schemasGrouped = getFeaturesSchema(defaults);

      const r = await NetworkAPI.getRawNetworkFeatures({ networkId });

      const { config } = await AutoPilotAPI.getAutoPilotConfig({
        networkId,
      });

      const rr = await NetworkAPI.getCstConfig({ networkId });
      const cstConfig = rr && rr.cstConfig ? rr.cstConfig : {};
      const filterEnable = _.keys(cstConfig.inventory_integration || {}).filter(
        (key) => cstConfig.inventory_integration[key].enable_optimization
      );

      const filterSettled = _.keys(
        cstConfig.inventory_integration || {}
      ).filter(
        (key) =>
          cstConfig.inventory_integration[key].enable_optimization &&
          (cstConfig.inventory_integration[key].opt_exp_traffic_ratio || 0) > 0
      );

      const optCheck = _.find(schemasGrouped, { key: "enableOptABTesting" });
      if (optCheck) {
        optCheck.msgFunc = (value, key) => {
          // enableOptABTesting: true / false
          // cst: all settled / all disabled / some settled

          // enableOptABTesting [true] + cst [all disabled]
          if (value === true && filterEnable.length === 0) {
            return [
              <span className="text-sm" key={`${key}-msg`}>
                At least one of Inventory Integrations must be enabled, and
                Optimization Experimental Traffic Ratio must be greater than 0
                in <CSTConfigLink networkId={networkId} />
              </span>,
              MESSAGE.WARNING,
            ];
          }

          // enableOptABTesting [true] + cst [some settled]
          if (
            value === true &&
            filterEnable.length !== 0 &&
            filterSettled.length !== filterEnable.length
          ) {
            return [
              <span className="text-sm" key={`${key}-msg`}>
                At least one of Inventory Integrations must be enabled, and
                Optimization Experimental Traffic Ratio set above 0 for it to
                take effect. <CSTConfigLink networkId={networkId} />
              </span>,
              MESSAGE.WARNING,
            ];
          }

          // enableOptABTesting [false] + cst [all settled]
          if (
            value !== true &&
            filterEnable.length !== 0 &&
            filterSettled.length === filterEnable.length
          ) {
            return [
              <span className="text-sm" key={`${key}-msg`}>
                Set this option to <b>true</b> to enable Opt AB Testing. (Since
                "Inventory Integrations" in{" "}
                <CSTConfigLink networkId={networkId} /> are properly
                configured.)
              </span>,
              MESSAGE.WARNING,
            ];
          }

          // enableOptABTesting [false] + cst [some settled]
          if (
            value !== true &&
            filterEnable.length !== 0 &&
            filterSettled.length !== filterEnable.length
          ) {
            return [
              <span className="text-sm" key={`${key}-msg`}>
                Set this option to <b>true</b> to enable Opt AB Testing. (Also
                "Inventory Integrations" in{" "}
                <CSTConfigLink networkId={networkId} /> are not properly
                configured.)
              </span>,
              MESSAGE.WARNING,
            ];
          }

          return [];
        };
      }

      const isKey20Nopb =
        config.prophet &&
        config.prophet.cst_nopb_traffic_report_source &&
        config.prophet.cst_nopb_traffic_report_source === "Key20NopbValue";

      _.forEach(
        ["enableCSTNopbKey20NopbValue", "enableCSTNopbKey20NopbValueReport"],
        (key) => {
          const item = _.find(schemasGrouped, { key });
          item.msgFunc = (value, key) => {
            if (value === true && !isKey20Nopb) {
              return [
                <span className="text-sm" key={`${key}-msg`}>
                  "Cst Nopb Traffic Report Source" should be set to
                  "Key20NopbValue" in <AutoPilotLink networkId={networkId} />
                </span>,
                MESSAGE.WARNING,
              ];
            } else if (value !== true && isKey20Nopb) {
              return [
                <span className="text-sm" key={`${key}-msg`}>
                  Set this option to <b>true</b> to complete the CST Nopb Key20
                  setup. (Since "Cst Nopb Traffic Report Source" in{" "}
                  <AutoPilotLink networkId={networkId} /> is set to
                  "Key20NopbValue")
                </span>,
                MESSAGE.WARNING,
              ];
            }

            return [];
          };
        }
      );

      this.setState({
        networkId,
        networkInfo,

        appName: r.app_name,
        // config: JSON.stringify(r.config, null, 4),
        // newConfig: JSON.stringify(r.config, null, 4),

        config: _.cloneDeep(r.config, null, 4),
        newConfig: _.cloneDeep(r.config, null, 4),
        updatedAt: r.updated_at,

        //new
        schemasGrouped,
        defaultConfig: defaults,

        isLoading: false,
        errMsg: null,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  handleConfigTextChanged(inputValue) {
    this.setState({ newConfig: inputValue });
  }

  async handleSaveRawFeatures() {
    this.setState({ isSaving: true });

    try {
      const { networkId, appName, newConfig } = this.state;
      const params = {
        networkId,
        appName,
        config: newConfig,
      };
      const r = await NetworkAPI.createNetworkFeatures(params);
      console.log("save result", r);
      this.setState({
        isSaving: false,
        saveErrMsg: null,
        config: _.cloneDeep(newConfig), // Clear diffs to prevent beforeunload alert
      });

      notify.show("Features saved! Automatically reloading page...", "success");

      setTimeout(() => {
        window.location.reload(false);
      }, 2000);
    } catch (err) {
      console.log("Failed to save network features", err);
      this.setState({
        isSaving: false,
        saveErrMsg: err.toString(),
      });
    }
  }

  handleApplyChanges({
    sectionConfigKey,
    configKey,
    newValue,
    isUseDefault,
    relatedConfig,
  }) {
    console.log("apply changes", {
      sectionConfigKey,
      configKey,
      newValue,
      relatedConfig,
      isUseDefault,
    });

    let newConfig = this.state.newConfig;

    function getConfigPath(configKey, sectionConfigKey = "") {
      return sectionConfigKey !== ""
        ? [sectionConfigKey, configKey]
        : configKey;
    }

    const configPathList = [
      {
        path: getConfigPath(configKey, sectionConfigKey),
        newValue,
        isUseDefault,
      },
      ...relatedConfig.map(({ prefix, key, newValue, isUseDefault }) => ({
        path: getConfigPath(key, prefix),
        newValue,
        isUseDefault,
      })),
    ];

    _.forEach(configPathList, ({ path, newValue, isUseDefault }) => {
      if (isUseDefault) {
        // remove from config
        _.unset(newConfig, path);
      } else {
        // add/edit to config
        _.set(newConfig, path, newValue);
      }
    });

    // empty keys with empty object or array value
    // eg. {"forerunner": {} } -> {}
    newConfig = _.omitBy(newConfig, (c) => {
      return typeof c === "object" && _.isEmpty(c);
    });

    this.setState({
      isEditing: false,
      newConfig,
    });
  }

  handleCloseReviewModal() {
    this.setState({ isReviewModalOpened: false });
  }

  handleOpenReviewModal() {
    this.setState({ isReviewModalOpened: true });
  }

  render() {
    const {
      networkInfo,

      config,
      updatedAt,

      isLoading,
      errMsg,

      newConfig,
      isSaving,
      saveErrMsg,

      schemasGrouped,
      isEditing,
      defaultConfig,

      isReviewModalOpened,
    } = this.state;

    return (
      <div>
        {isLoading && <LoadingUI></LoadingUI>}
        {errMsg && <div className="text-red-800">{errMsg}</div>}

        {networkInfo && (
          <div>
            <NetworkInfoHeader networkInfo={networkInfo}></NetworkInfoHeader>
            <div className="min-h-screen bg-white px-4 py-8">
              <div className="flex w-3/4 items-center justify-between pl-8">
                <PageTitle title="Network Features"></PageTitle>
                <button
                  type="button"
                  className="rounded flex items-center gap-1 px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-gray-100"
                  onClick={() =>
                    window.open(
                      `${window.location.origin}/#/ops-mgmt/account/publisher/${networkInfo.pubId}/features`
                    )
                  }
                >
                  Publisher Features
                  <FiExternalLink></FiExternalLink>
                </button>
              </div>

              <div className="flex w-full gap-4">
                <div className="mt-2 w-3/4">
                  <SchemaConfigTable
                    configSchemas={schemasGrouped}
                    newConfig={newConfig}
                    handleApplyChanges={(params) =>
                      this.handleApplyChanges({
                        ...params,
                        sectionConfigKey: params.prefix,
                      })
                    }
                    handleStartEditMode={() =>
                      this.setState({ isEditing: true })
                    }
                    handleCancelEditMode={() =>
                      this.setState({ isEditing: false })
                    }
                  ></SchemaConfigTable>
                  {/* <TextAreaWithJsonCheck
                    rows={18}
                    inputValue={config}
                    handleInputChanged={this.handleConfigTextChanged}
                  ></TextAreaWithJsonCheck> */}
                </div>

                <div className="w-1/4">
                  {updatedAt && (
                    <div className="mb-4 text-sm text-gray-600">
                      Last update time:
                      <br />
                      <DateTimeFormatter
                        datetime={updatedAt}
                        timezone={networkInfo.timezone}
                      ></DateTimeFormatter>
                    </div>
                  )}

                  <div
                    className="overflow-y-auto"
                    style={{ maxHeight: "420px" }}
                  >
                    <ConfigDiffView
                      type="FEATURES"
                      originalConfig={config}
                      newConfig={newConfig}
                      defaultConfig={defaultConfig}
                    ></ConfigDiffView>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      className={`shadow rounded px-8 py-3 font-semibold ${
                        isSaving || isEditing
                          ? "cursor-not-allowed bg-gray-400 text-white"
                          : "bg-blue-400 text-white hover:bg-blue-600"
                      }`}
                      // onClick={this.handleSaveRawFeatures}
                      onClick={this.handleOpenReviewModal}
                      disabled={isSaving || isEditing}
                    >
                      Preview then save features
                    </button>
                  </div>
                  <div className="font-semibold text-red-600">{saveErrMsg}</div>
                </div>
              </div>

              <PreviewFeaturesAndSaveModal
                finalConfig={newConfig}
                isReviewModalOpened={isReviewModalOpened}
                handleCloseReviewModal={this.handleCloseReviewModal}
                isSaving={isSaving}
                saveErrMsg={saveErrMsg}
                handleConfirmSaveConfig={this.handleSaveRawFeatures}
              ></PreviewFeaturesAndSaveModal>
            </div>
          </div>
        )}
      </div>
    );
  }
}

class CSTConfigLink extends React.Component {
  render() {
    const { networkId } = this.props;
    return (
      <a
        className="text-blue-600 hover:text-blue-800"
        href={`${window.location.origin}/#/ops-mgmt/account/network/${networkId}/cst-config`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Network CST Config
      </a>
    );
  }
}

class AutoPilotLink extends React.Component {
  render() {
    const { networkId } = this.props;
    return (
      <a
        className="text-blue-600 hover:text-blue-800"
        href={`${window.location.origin}/#/control-center/network/${networkId}/auto-pilot/config`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Network AutoPilot Config
      </a>
    );
  }
}

export default NetworkFeaturesViewer;
